/**
 * Date Utilities
 * Provides standardized date formatting functions for the application
 * Follows architectural patterns defined in guardrails
 */

/**
 * Format a date string to a human-readable format
 * @param dateString ISO date string or null/undefined
 * @param options Intl.DateTimeFormatOptions for customizing the output
 * @returns Formatted date string or placeholder if date is null
 */
export function formatDate(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string {
  if (!dateString) {
    return 'Not specified';
  }

  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    return new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {

    return 'Error formatting date';
  }
}

/**
 * Format a date to a relative timeframe (e.g., "2 hours ago")
 * @param dateString ISO date string or null/undefined
 * @returns Relative time string or placeholder if date is null
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) {
    return 'Not specified';
  }

  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffInMilliseconds = now.getTime() - date.getTime();
    
    // Convert to seconds
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
    
    // Less than a minute
    if (diffInSeconds < 60) {
      return diffInSeconds <= 5 ? 'Just now' : `${diffInSeconds} seconds ago`;
    }
    
    // Less than an hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    
    // Less than a day
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    
    // Less than a week
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    }
    
    // Less than a month (approximation)
    if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
    
    // Less than a year (approximation)
    if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    }
    
    // More than a year
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  } catch (error) {

    return 'Error formatting date';
  }
}

/**
 * Check if a date is in the past
 * @param dateString ISO date string or null/undefined
 * @returns Boolean indicating if date is in the past, or false if date is null
 */
export function isDateInPast(dateString: string | null | undefined): boolean {
  if (!dateString) {
    return false;
  }

  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return false;
    }
    
    return date < new Date();
  } catch (error) {

    return false;
  }
}
